import React, { useState, useRef, useEffect } from 'react';
import './homeIndex.css';
import { motion, AnimatePresence } from 'framer-motion';
import C2wLanding from '../../components/C2wHome/landing/landing';
import C2wTechnologies from '../../components/C2wHome/technologies/technologies';
import C2wTour from '../../components/C2wHome/tour/tour';
import C2wIncubators from '../../components/C2wHome/incubators/incubators';
import { useSelector } from 'react-redux';
import Footer from './Footer';
import C2wCourses from '../../components/C2wHome/courses/courses';
import C2wAppIntro from '../../components/C2wHome/AppIntroAndReview/AppIntro/appIntro';
import C2wCompanies from '../../components/C2wHome/companies/companies';
import C2wPlacements from '../../components/C2wHome/placements/placements';
import { useLocation, useNavigate } from 'react-router';
import C2wReviews from '../../components/C2wHome/AppIntroAndReview/Review/review';
import C2wContactUsHome from './contactUs/contactUsHome';
import { Helmet } from 'react-helmet';
import { Element } from 'react-scroll';
import C2wEventsHome from './events/EventsHome';
import C2GoogleReviewTemplate from '../../components/C2wHome/googleReviewsTemplate/googleReviewTemplate';
import C2wGoogleReviewTab from '../../components/C2wHome/googleReviewTab/googleReviewTab';
import C2wSuperxTestimonials from '../../components/C2wSuperx/testimonials/testimonials';
import C2wLocation from '../aboutus/location/Location';
import C2wGoogleReviews from '../aboutus/googleReviews/GoogleReviews';
import C2wPromoBanner from '../../components/C2wHome/PromoBanner/C2wPromoBanner';
import C2wInClassActivities from '../../components/C2wHome/inClassActivities/inClassActivities';
import C2wSponsorship from '../../components/C2wHome/sponsorship/sponsorship';
import C2wEntrepreneurship from '../../components/C2wHome/entrepreneurship/entrepreneurship';

function useScrollAnimation() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);


  /**
 * @author Vishal Lohbande
 * @hook useEffect - Visibility Observer
 *
 * useEffect - Observes a referenced DOM element and sets its visibility state based on intersection with viewport.
 *
 * @ref {React.RefObject} ref - A reference to the DOM element to be observed.
 * @state {Function} setIsVisible - A state setter function to update visibility when the element is in view.
 *
 * Behavior:
 * - Initializes an IntersectionObserver with a threshold of 0.3 (30% visibility).
 * - When the element becomes at least 30% visible in the viewport:
 *   - Updates visibility state to `true` using `setIsVisible`.
 *   - Automatically disconnects the observer to prevent repeated triggers.
 *
 * Clean-Up:
 * - Ensures the observer is disconnected when the component unmounts to prevent memory leaks.
 *
 * Notes:
 * - Useful for triggering animations or loading content when elements scroll into view.
 * - Threshold value can be adjusted to make visibility detection more or less sensitive.
 */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
}


/**
 * @component AnimatedComponent
 * @author Vishal Lohbande
 * AnimatedComponent - A reusable motion wrapper that triggers entrance animation 
 * when the element scrolls into view.
 *
 * @param {Function} children - A render function that receives `isVisible` as an argument.
 *                              This allows conditional rendering based on visibility state.
 *
 * Hooks Used:
 * - `useScrollAnimation`: Custom hook that returns a `ref` and `isVisible` boolean.
 *   - `ref` is attached to the DOM element to be observed.
 *   - `isVisible` becomes `true` when the element is at least partially visible.
 *
 * Behavior:
 * - Wraps children in a `motion.div` (Framer Motion).
 * - Applies a fade-in + upward slide animation (`opacity: 0 → 1`, `y: 50 → 0`).
 * - Animation is triggered only once, when the component scrolls into view.
 * - Uses smooth `easeOut` transition with a duration of 0.6 seconds.
 *
 * Returns:
 * - A motion-animated wrapper that enhances child components with scroll-based visibility animation.
 *
 * Example Usage:
 * <AnimatedComponent>
 *   {(isVisible) => <h2>{isVisible ? "Now in view!" : "Waiting..."}</h2>}
 * </AnimatedComponent>
 */
function AnimatedComponent({ children }) {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {children(isVisible)}
    </motion.div>
  );
}

function C2wHomeindex() {
  const settingFlag = useSelector((state) => state.data.settingFlag)
  const incubatorsRef = useRef(null);
  const placementsRef = useRef(null)
  const companiesRef = useRef(null)
  const location = useLocation()
  const navigate = useNavigate()
  const [googleReviewTab, setGoogleReviewTab] = useState(false)
  const [showBanner, setShowBanner] = useState(true);


  /**
 * @function scrollToIncubators
 * @author Vishal Lohbande
 *
 * scrollToIncubators - Smoothly scrolls the page to the DOM element referenced by `incubatorsRef`.
 *
 * Behavior:
 * - Waits for 100 milliseconds before executing (to allow time for DOM updates, layout changes, or transitions).
 * - Checks if `incubatorsRef.current` is available.
 * - If the reference is valid, smoothly scrolls to the element with alignment to the top (`block: "start"`).
 *
 * Usage Scenario:
 * - Typically used when triggering scroll after an async action (e.g., tab switch, route change, or modal close).
 * - Ensures a smooth user experience by animating the scroll movement.
 *
 * Notes:
 * - Uses `scrollIntoView` with `behavior: "smooth"` to enable native smooth scrolling.
 * - The 100ms delay can be adjusted based on when the target element becomes ready in the DOM.
 */
  const scrollToIncubators = () => {
    setTimeout(() => {
      if (incubatorsRef.current) {
        incubatorsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };


  /**
 * @function scrollToPlacements
 * @author Vishal Lohbande
 * @Date 28-05-2025
 *
 * scrollToPlacements - Smoothly scrolls the page to the DOM element referenced by `placementsRef`.
 *
 * Behavior:
 * - Waits for 100 milliseconds before executing (to allow time for DOM updates, layout changes, or transitions).
 * - Checks if `placementsRef.current` is available.
 * - If the reference is valid, smoothly scrolls to the element with alignment to the top (`block: "start"`).
 *
 * Usage Scenario:
 * - Typically used when triggering scroll after an async action (e.g., tab switch, route change, or modal close).
 * - Ensures a smooth user experience by animating the scroll movement.
 *
 * Notes:
 * - Uses `scrollIntoView` with `behavior: "smooth"` to enable native smooth scrolling.
 * - The 100ms delay can be adjusted based on when the target element becomes ready in the DOM.
 */
  const scrollToPlacemnets = () => {
    navigate('/placements');
  }

  /**
 * @function scrollToCompanies
 * @author Vishal Lohbande
 * @Date 28-05-2025
 * scrollToCompanies - Smoothly scrolls the page to the DOM element referenced by `companiesRef`.
 *
 * Behavior:
 * - Waits for 100 milliseconds before executing (to allow time for DOM updates, layout changes, or transitions).
 * - Checks if `companiesRef.current` is available.
 * - If the reference is valid, smoothly scrolls to the element with alignment to the top (`block: "start"`).
 *
 * Usage Scenario:
 * - Typically used when triggering scroll after an async action (e.g., tab switch, route change, or modal close).
 * - Ensures a smooth user experience by animating the scroll movement.
 *
 * Notes:
 * - Uses `scrollIntoView` with `behavior: "smooth"` to enable native smooth scrolling.
 * - The 100ms delay can be adjusted based on when the target element becomes ready in the DOM.
 */
  const scrollToCompanies = () => {
    setTimeout(() => {
      if (companiesRef.current) {
        companiesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100)
  }

  useEffect(() => {
    // You can trigger animations, scroll to top, fetch data again, etc.
  }, [location.state?.forceRemount]);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className='C2wAdHome_index' data-lenis-prevent>
      <Helmet>
        {/* 
        Purpose: Defines the page title displayed in search results and browser tabs. 
                 It’s one of the most important SEO elements. 
        */}
        <title>{`Learn Coding Languages with Core2web`}</title>

        {/* 
        Purpose: Provides a brief summary of the page content in search results. 
                 While not a direct ranking factor, it influences CTR. 
        */}
        <meta name="description" content="High-quality Course with best Content." />

        {/* Purpose: Controls indexing and link-following behavior. */}
        <meta name="robots" content="index, follow" />

      </Helmet>
      <div className='C2wAdHome_index_scroller'>

        {/* { googleReviewTab && <C2GoogleReviewTemplate setGoogleReviewTab={setGoogleReviewTab} />}  
        { windowWidth > 768 && <C2wGoogleReviewTab setGoogleReviewTab={setGoogleReviewTab} /> } */}
        {/* 
            /**
         * @component <C2wLanding />
         * @author Vishal Lohbande
         *
         * Description:
         * - Renders the `C2wLanding` component and passes a scroll handler via props.
         *
         * Props:
         * - `onIncubatorsClick`: A callback function (`scrollToIncubators`) that smoothly scrolls
         *   the page to the incubators section when triggered (e.g., via a button inside `C2wLanding`).
         *
         * Behavior:
         * - When a user interacts with a CTA or button inside `C2wLanding` (typically labeled "Incubators"),
         *   the `scrollToIncubators` function is executed to scroll to the referenced section.
         *
         * Usage Context:
         * - Used when the landing page contains internal navigation to scroll to specific sections on user action.
         *
         * Example Trigger Flow:
         * → Button Click inside C2wLanding → Triggers `onIncubatorsClick` → Smooth scrolls to section via `scrollToIncubators`
        {/* Promotional Java 2026 Floating Banner */}
        <AnimatePresence>
          {showBanner && <C2wPromoBanner onClose={() => setShowBanner(false)} />}
        </AnimatePresence>


        <C2wLanding
          onCompaniesClick={scrollToCompanies}
          onPlacementsClick={scrollToPlacemnets}
          onIncubatorsClick={scrollToIncubators} />
        <C2wCourses />
        <C2wInClassActivities />
        <C2wSponsorship />
        <C2wEntrepreneurship />
        <div ref={companiesRef}>
          <Element name='companies'>
            <C2wCompanies />
          </Element>
        </div>
        <C2wPlacements />
        <C2wTour />
        <C2wEventsHome />
        <AnimatedComponent>
          {(isVisible) => <C2wTechnologies isVisible={isVisible} />}
        </AnimatedComponent>

        <div ref={incubatorsRef}>
          <C2wIncubators />
        </div>
        <C2wSuperxTestimonials />
        <C2wAppIntro />
        <C2wReviews />
        <C2wGoogleReviews />
        {/* {windowWidth < 768 && <C2wGoogleReviewTab setGoogleReviewTab={setGoogleReviewTab} />} */}
        <C2wLocation />
        <C2wContactUsHome />
        <div className="ADfooter__container">
          <Footer />
        </div>
      </div>
    </div>
  )
}

export default C2wHomeindex;
